import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  FLOW_GATEWAY,
  IWHCreateMaintenanceNotificationCommand,
  IWHPageableMaintenanceNotificationsDTO,
  WHHttpParams,
  WHMaintenanceNotificationDOM,
  initHttpParams,
} from '@workheld/workheld-shared-lib';

import { environment } from '../../environments/environment';

/**
 * Editable payload of `POST /maintenancenotifications/{id}` (WHP-12150).
 * ENUM fields (type, category, errorCode) are intentionally absent — they are
 * not editable after creation.
 */
export interface IWHUpdateMaintenanceNotificationPayload {
  maintenanceNotificationStatus?: string;
  title?: string;
  /** Legacy wire name for the MN description column (WHP-11335). */
  comment?: string;
  managerComment?: string;
  contactPerson?: string;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceNotificationAsyncService {
  constructor(private httpClient: HttpClient) {}

  public getMaintenanceNotifications(
    params: WHHttpParams,
  ): Observable<IWHPageableMaintenanceNotificationsDTO> {
    const httpParams: HttpParams = initHttpParams(params);

    return this.httpClient.get<IWHPageableMaintenanceNotificationsDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications`,
      { params: httpParams },
    );
  }

  public getMaintenanceNotificationsByTeam(
    teamId: string,
    params: WHHttpParams,
  ): Observable<IWHPageableMaintenanceNotificationsDTO> {
    const httpParams: HttpParams = initHttpParams(params);

    return this.httpClient.get<IWHPageableMaintenanceNotificationsDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/team/${teamId}`,
      { params: httpParams },
    );
  }

  public getMaintenanceNotification(
    maintenanceNotificationId: string,
  ): Observable<WHMaintenanceNotificationDOM> {
    return this.httpClient.get<WHMaintenanceNotificationDOM>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/${maintenanceNotificationId}`,
    );
  }

  public dispatchMaintenanceNotification(
    maintenanceNotificationId: string,
  ): Observable<WHMaintenanceNotificationDOM> {
    return this.httpClient.post<WHMaintenanceNotificationDOM>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/${maintenanceNotificationId}/dispatch`,
      {},
    );
  }

  public createMaintenanceNotification(
    payload: IWHCreateMaintenanceNotificationCommand,
  ): Observable<WHMaintenanceNotificationDOM> {
    return this.httpClient.post<WHMaintenanceNotificationDOM>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications`,
      payload,
    );
  }

  public manualUpdateMaintenanceNotification(
    maintenanceNotificationId: string,
    payload: IWHUpdateMaintenanceNotificationPayload,
  ): Observable<WHMaintenanceNotificationDOM> {
    return this.httpClient.post<WHMaintenanceNotificationDOM>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/${maintenanceNotificationId}`,
      payload,
    );
  }

  /**
   * Attaches a single file to an existing MN. Unlike the unified `/v1/files`
   * upload used on create, this endpoint is permission- and lifecycle-guarded
   * on the backend (WHP-12150).
   */
  public uploadMaintenanceNotificationFile(
    maintenanceNotificationId: string,
    fileToUpload: File,
  ): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);

    return this.httpClient.post(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/${maintenanceNotificationId}/files`,
      formData,
    );
  }

  public deleteMaintenanceNotificationFile(fileId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${environment.apiUrl + FLOW_GATEWAY}/maintenancenotifications/files/${fileId}`,
    );
  }
}
